import React from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

import { Label } from '../../components/ui/label';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';

import { Edit2, FileText, Download, Upload, Calculator, Eye, BarChart3, FileJson, FileSpreadsheet, Building, Tag } from 'lucide-react';
import { ACTIVITY_STATUS, STATUS_BADGE_COLORS } from './nomenclatureConstants';
import { summarizeExport } from './nomenclatureHelpers';
import NomenclatureFormDialog from './NomenclatureFormDialog';

function getStatusBadge(status) {
  const cfg = ACTIVITY_STATUS.find((s) => s.value === status) || ACTIVITY_STATUS[0];
  const Icon = cfg.icon;
  const cls = STATUS_BADGE_COLORS[cfg.color] || STATUS_BADGE_COLORS.gray;
  return (<Badge className={cls}><Icon className="h-3 w-3 mr-1" />{cfg.label}</Badge>);
}

export default function NomenclatureModals({ ctx }) {
  const { nomenclatures: _nomenclatures, categories: categories, dispositifs: dispositifs, estimateurs: estimateurs, dispositifSearch: dispositifSearch, setDispositifSearch: setDispositifSearch, estimateurSearch: estimateurSearch, setEstimateurSearch: setEstimateurSearch, selectedIds, selectionMode, createDialogOpen: createDialogOpen, setCreateDialogOpen: setCreateDialogOpen, editDialogOpen: editDialogOpen, setEditDialogOpen: setEditDialogOpen, viewDialogOpen, setViewDialogOpen, importDialogOpen, setImportDialogOpen, exportDialogOpen, setExportDialogOpen, statsDialogOpen, setStatsDialogOpen, currentItem, formData: _formData, setFormData: _setFormData, stats, fileInputRef, filteredNomenclatures, handleCreate: _handleCreate, handleUpdate: _handleUpdate, handleExport, handleImport, resetForm: _resetForm, openEditDialog } = ctx;
  const selectedCollection = ctx.selectedCollection;
  const exportPrefsKey = selectedCollection?.id ? `nomenclature_export_sheets_${selectedCollection.id}` : null;
  const [excelSheetOptions, setExcelSheetOptions] = React.useState({ categories: null, dispositifs: null });

  // Recharge le dernier choix d'onglets mémorisé pour la collection courante
  React.useEffect(() => {
    if (!exportPrefsKey) return;
    try {
      const saved = localStorage.getItem(exportPrefsKey);
      setExcelSheetOptions(saved ? JSON.parse(saved) : { categories: null, dispositifs: null });
    } catch {
      setExcelSheetOptions({ categories: null, dispositifs: null });
    }
  }, [exportPrefsKey]);

  // Mémorise le choix d'onglets par collection
  React.useEffect(() => {
    if (!exportPrefsKey) return;
    try { localStorage.setItem(exportPrefsKey, JSON.stringify(excelSheetOptions)); } catch { /* noop */ }
  }, [exportPrefsKey, excelSheetOptions]);

  return (
    <>
      <NomenclatureFormDialog ctx={ctx} />

      {/* Dialog Vue détaillée */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Détails de la nomenclature
            </DialogTitle>
          </DialogHeader>
          
          {currentItem && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="font-mono text-lg px-3 py-1 bg-slate-700 text-white border-slate-800 font-semibold">{currentItem.code}</Badge>
                {getStatusBadge(currentItem.status || (currentItem.is_active ? 'active' : 'inactive'))}
              </div>
              
              <h2 className="text-xl font-bold text-slate-800">{currentItem.name}</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <Label className="text-slate-500 text-xs">Catégorie</Label>
                  <p className="font-medium">{currentItem.category}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <Label className="text-slate-500 text-xs">Applicable Devenir Membre</Label>
                  <p className="font-medium">{currentItem.applicable_devenir_membre ? 'Oui' : 'Non'}</p>
                </div>
              </div>
              
              {currentItem.definition && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <Label className="text-blue-700 text-xs mb-2 block">Définition</Label>
                  <p className="text-slate-700 whitespace-pre-wrap">{currentItem.definition}</p>
                </div>
              )}
              
              {currentItem.exclusions && (
                <div className="p-4 bg-amber-50 rounded-lg">
                  <Label className="text-amber-700 text-xs mb-2 block">Exclusions</Label>
                  <p className="text-slate-700 whitespace-pre-wrap">{currentItem.exclusions}</p>
                </div>
              )}
              
              {(currentItem.dispositifs?.length > 0 || currentItem.estimateurs?.length > 0) && (
                <div className="grid grid-cols-2 gap-4">
                  {currentItem.dispositifs?.length > 0 && (
                    <div className="p-3 border rounded-lg">
                      <Label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                        <Building className="h-3 w-3" /> Dispositifs
                      </Label>
                      <div className="flex flex-wrap gap-1">
                        {currentItem.dispositifs.map((d, i) => (
                          <Badge key={i} variant="outline" className="bg-blue-700 text-white border-blue-800 font-semibold">{d}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {currentItem.estimateurs?.length > 0 && (
                    <div className="p-3 border rounded-lg">
                      <Label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                        <Calculator className="h-3 w-3" /> Estimateurs
                      </Label>
                      <div className="flex flex-wrap gap-1">
                        {currentItem.estimateurs.map((e, i) => (
                          <Badge key={i} variant="outline" className="bg-purple-700 text-white border-purple-800 font-semibold">{e}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Fermer</Button>
            <Button onClick={() => { setViewDialogOpen(false); openEditDialog(currentItem); }}>
              <Edit2 className="h-4 w-4 mr-2" /> Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Export */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Exporter les nomenclatures
            </DialogTitle>
            <DialogDescription>
              {selectionMode && selectedIds.size > 0 
                ? `Exporter ${selectedIds.size} élément(s) sélectionné(s)`
                : `Exporter ${filteredNomenclatures.length} élément(s) filtrés`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-3 gap-4 py-6">
            <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => handleExport('json')}>
              <FileJson className="h-8 w-8 text-amber-600" />
              <span>JSON</span>
            </Button>
            <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => handleExport('csv')}>
              <FileText className="h-8 w-8 text-green-600" />
              <span>CSV</span>
            </Button>
            <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => handleExport('excel', excelSheetOptions)}>
              <FileSpreadsheet className="h-8 w-8 text-emerald-600" />
              <span>Excel</span>
            </Button>
          </div>

          {/* Sélection ciblée des onglets Excel */}
          {(() => {
            const exportData = selectionMode && selectedIds.size > 0
              ? filteredNomenclatures.filter((n) => selectedIds.has(n.id))
              : filteredNomenclatures;
            const summary = summarizeExport(exportData);
            const catSel = excelSheetOptions?.categories;
            const dispSel = excelSheetOptions?.dispositifs;
            const isCatOn = (name) => !catSel || catSel.includes(name);
            const isDispOn = (name) => !dispSel || dispSel.includes(name);
            const toggleCat = (name) => {
              const base = catSel || summary.categories.map((c) => c.name);
              const next = base.includes(name) ? base.filter((x) => x !== name) : [...base, name];
              setExcelSheetOptions((o) => ({ ...o, categories: next }));
            };
            const toggleDisp = (name) => {
              const base = dispSel || summary.dispositifs.map((d) => d.name);
              const next = base.includes(name) ? base.filter((x) => x !== name) : [...base, name];
              setExcelSheetOptions((o) => ({ ...o, dispositifs: next }));
            };
            const selectedCatCount = catSel ? catSel.length : summary.categories.length;
            const selectedDispCount = dispSel ? dispSel.length : summary.dispositifs.length;
            const sheetCount = 1 + selectedCatCount + selectedDispCount;
            return (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="export-excel-preview">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 text-emerald-800">
                    <FileSpreadsheet className="h-4 w-4" />
                    <span className="text-sm font-semibold">Fichier Excel — {sheetCount} onglet(s)</span>
                  </div>
                  {(catSel || dispSel) && (
                    <button
                      type="button"
                      onClick={() => setExcelSheetOptions({ categories: null, dispositifs: null })}
                      className="text-[11px] text-emerald-700 hover:underline font-semibold"
                      data-testid="export-reset-selection"
                    >
                      Tout inclure
                    </button>
                  )}
                </div>
                <p className="text-xs text-emerald-700 mb-3">
                  1 onglet « Toutes » ({summary.total} lignes) + {selectedCatCount} par catégorie + {selectedDispCount} par dispositif. Cliquez pour inclure/exclure un onglet.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600 mb-1">Par catégorie</p>
                    <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                      {summary.categories.map((c) => (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => toggleCat(c.name)}
                          data-testid={`export-cat-${c.name}`}
                          className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${isCatOn(c.name) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-500 border-slate-200 line-through'}`}
                        >
                          {c.name} · {c.count}
                        </button>
                      ))}
                      {summary.categories.length === 0 && <span className="text-[11px] text-emerald-600">Aucune</span>}
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600 mb-1">Par dispositif</p>
                    <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                      {summary.dispositifs.map((d) => (
                        <button
                          key={d.name}
                          type="button"
                          onClick={() => toggleDisp(d.name)}
                          data-testid={`export-disp-${d.name}`}
                          className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${isDispOn(d.name) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-500 border-slate-200 line-through'}`}
                        >
                          {d.name} · {d.count}
                        </button>
                      ))}
                      {summary.dispositifs.length === 0 && <span className="text-[11px] text-emerald-600">Aucun</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Dialog Import */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Importer des nomenclatures
            </DialogTitle>
            <DialogDescription>
              Formats supportés : JSON, CSV
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <div 
              className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-indigo-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600 mb-2">Cliquez ou glissez-déposez un fichier</p>
              <p className="text-sm text-slate-400">JSON ou CSV</p>
            </div>
            <input 
              ref={fileInputRef}
              type="file" 
              accept=".json,.csv" 
              onChange={handleImport}
              className="hidden" 
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Statistiques */}
      <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Statistiques des nomenclatures
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Stats globales */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <div className="text-3xl font-bold text-blue-700">{stats.total}</div>
                <div className="text-sm text-blue-600">Total</div>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <div className="text-3xl font-bold text-green-700">{stats.active}</div>
                <div className="text-sm text-green-600">Actifs</div>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <div className="text-3xl font-bold text-amber-700">{stats.pending}</div>
                <div className="text-sm text-amber-600">En attente</div>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
                <div className="text-3xl font-bold text-slate-700">{stats.archived}</div>
                <div className="text-sm text-slate-600">Archivés</div>
              </Card>
            </div>
            
            {/* Par catégorie */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Tag className="h-4 w-4" /> Par catégorie
              </h4>
              <div className="space-y-2">
                {Object.entries(stats.byCategory).map(([cat, count]) => (
                  <div key={cat} className="flex items-center gap-3">
                    <span className="text-sm text-slate-600 w-40 truncate">{cat}</span>
                    <div className="flex-1 bg-slate-600 text-white rounded-full h-2">
                      <div 
                        className="bg-indigo-500 rounded-full h-2" 
                        style={{ width: `${(count / stats.total) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-10 text-right">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Par dispositif */}
            {Object.keys(stats.byDispositif).length > 0 && (
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Building className="h-4 w-4" /> Par dispositif
                </h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.byDispositif).map(([disp, count]) => (
                    <Badge key={disp} variant="outline" className="px-3 py-1 bg-green-700 text-white border-green-800 font-semibold">
                      {disp}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

